import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import type { CoreOrganization } from '@/lib/zifekCore';
import {
  createCoreDocument,
  deleteCoreDocument,
  DOCUMENT_ENTITY_TYPES,
  entityTypeMeta,
  listCoreDocuments,
  updateCoreDocument,
  VISIBILITIES,
  visibilityMeta,
  type CoreDocument,
} from '@/lib/coreSocle';
import Modal from '@/pages/dashboard/core/components/Modal';

interface Props {
  commerceId: number;
  org: CoreOrganization;
}

const INPUT = 'w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors';

interface FormState {
  id: number | null;
  name: string;
  entityType: string;
  entityId: string;
  category: string;
  visibility: string;
  fileId: string;
}

const EMPTY: FormState = { id: null, name: '', entityType: '', entityId: '', category: '', visibility: 'private', fileId: '' };

export default function DocumentsTab({ commerceId, org }: Props) {
  const { user } = useAuth();
  const [docs, setDocs] = useState<CoreDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [filter, setFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setDocs(await listCoreDocuments(commerceId));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [commerceId]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm(EMPTY); setOpen(true); };
  const openEdit = (d: CoreDocument) => {
    setForm({
      id: d.id,
      name: d.name,
      entityType: d.entity_type || '',
      entityId: d.entity_id || '',
      category: d.category || '',
      visibility: d.visibility || 'private',
      fileId: d.file_id || '',
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      if (form.id) {
        await updateCoreDocument(form.id, {
          name: form.name.trim(),
          entityType: form.entityType || null,
          entityId: form.entityId.trim() || null,
          category: form.category.trim() || null,
          visibility: form.visibility,
          fileId: form.fileId.trim() || null,
        });
      } else {
        await createCoreDocument({
          ownerId: commerceId,
          ownerUserId: String(user?.id ?? commerceId),
          name: form.name.trim(),
          entityType: form.entityType || null,
          entityId: form.entityId.trim() || null,
          category: form.category.trim() || null,
          visibility: form.visibility,
          fileId: form.fileId.trim() || null,
        });
      }
      setOpen(false);
      load();
    } finally {
      setSaving(false);
    }
  };

  const remove = async (d: CoreDocument) => {
    setBusyId(d.id);
    try {
      await deleteCoreDocument(d.id);
      load();
    } finally {
      setBusyId(null);
    }
  };

  const visible = filter === 'all' ? docs : docs.filter((d) => (d.entity_type || 'none') === filter);

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 w-fit overflow-x-auto">
          <button onClick={() => setFilter('all')} className={`px-3.5 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${filter === 'all' ? 'bg-background-50 text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}>Tous</button>
          {DOCUMENT_ENTITY_TYPES.map((t) => (
            <button key={t.key} onClick={() => setFilter(t.key)} className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${filter === t.key ? 'bg-background-50 text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}>{t.label}</button>
          ))}
        </div>
        <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer">
          <i className="ri-add-line"></i> Nouveau document
        </button>
      </div>

      <p className="text-xs text-foreground-500 mb-4">
        Couche documentaire centrale — un document peut être rattaché à un utilisateur, une entreprise, un client, une commande, une facture, un projet, une réservation, un employé, un produit ou un service. Les fichiers restent dans le stockage Zifek.
      </p>

      {error && <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}

      {loading ? (
        <div className="flex items-center justify-center py-16"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
      ) : visible.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-background-50 border border-background-200/70 rounded-lg">
          <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3"><i className="ri-file-text-line text-2xl text-foreground-400"></i></div>
          <p className="text-foreground-600">Aucun document pour ce filtre.</p>
        </div>
      ) : (
        <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead>
                <tr className="text-left text-xs text-foreground-500 bg-background-100/70">
                  <th className="px-4 py-2.5 font-medium">Document</th>
                  <th className="px-4 py-2.5 font-medium">Rattaché à</th>
                  <th className="px-4 py-2.5 font-medium">Catégorie</th>
                  <th className="px-4 py-2.5 font-medium">Visibilité</th>
                  <th className="px-4 py-2.5 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((d) => {
                  const ent = entityTypeMeta(d.entity_type);
                  const vis = visibilityMeta(d.visibility);
                  return (
                    <tr key={d.id} className="border-t border-background-200/60 hover:bg-background-100/40 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 min-w-0">
                          <i className="ri-file-3-line text-primary-500"></i>
                          <span className="font-medium text-foreground-900 truncate">{d.name}</span>
                        </div>
                        {d.file_id && <span className="text-[10px] text-foreground-400 font-mono">#{d.file_id}</span>}
                      </td>
                      <td className="px-4 py-3 text-foreground-600">
                        {ent ? (
                          <span className="inline-flex items-center gap-1.5 text-xs">
                            <i className={ent.icon}></i>{ent.label}{d.entity_id ? ` · ${d.entity_id}` : ''}
                          </span>
                        ) : <span className="text-xs text-foreground-400">—</span>}
                      </td>
                      <td className="px-4 py-3 text-xs text-foreground-600">{d.category || '—'}</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900"><i className={vis.icon}></i>{vis.label}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-1">
                          <button onClick={() => openEdit(d)} className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 hover:text-foreground-900 transition-colors cursor-pointer"><i className="ri-edit-line"></i></button>
                          <button onClick={() => remove(d)} disabled={busyId === d.id} className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-400 hover:bg-red-50 hover:text-red-600 transition-colors cursor-pointer disabled:opacity-50"><i className="ri-delete-bin-line"></i></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {open && (
        <Modal
          title={form.id ? 'Modifier le document' : 'Nouveau document'}
          onClose={() => setOpen(false)}
          maxWidth="max-w-xl"
          footer={
            <>
              <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-full text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Annuler</button>
              <button onClick={save} disabled={saving || !form.name.trim()} className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2">
                {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>} Enregistrer
              </button>
            </>
          }
        >
          <div className="space-y-3">
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Nom du document</span>
              <input className={INPUT} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex : Contrat de prestation" autoFocus />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="block text-xs font-medium text-foreground-600 mb-1">Rattaché à</span>
                <select className={INPUT} value={form.entityType} onChange={(e) => setForm({ ...form, entityType: e.target.value })}>
                  <option value="">Aucune</option>
                  {DOCUMENT_ENTITY_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-foreground-600 mb-1">Référence (id)</span>
                <input className={INPUT} value={form.entityId} onChange={(e) => setForm({ ...form, entityId: e.target.value })} placeholder="Ex : 42" />
              </label>
            </div>
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Catégorie</span>
              <input className={INPUT} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Ex : Contrats" />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Référence fichier (stockage Zifek)</span>
              <input className={INPUT} value={form.fileId} onChange={(e) => setForm({ ...form, fileId: e.target.value })} placeholder="Chemin / identifiant de stockage" />
            </label>
            <div>
              <span className="block text-xs font-medium text-foreground-600 mb-1">Visibilité</span>
              <div className="flex flex-wrap gap-2">
                {VISIBILITIES.map((v) => (
                  <button key={v.key} type="button" onClick={() => setForm({ ...form, visibility: v.key })} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition-colors cursor-pointer ${form.visibility === v.key ? 'border-primary-300 bg-primary-50 text-primary-700' : 'border-background-200/70 text-foreground-600 hover:bg-background-100'}`}>
                    <i className={v.icon}></i>{v.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}